import { useAutoneTranslation } from '@autone/translations';
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableContainer,
  DataTableContainerContent,
  DataTableContainerFooter,
  DataTableHead,
  DataTableHeader,
  DataTablePagination,
  DataTableRow,
  useColumnVisibilityAndOrderState,
  useDataTableLoadingGuard,
  useDebounceEffect,
  useServerSorting,
} from '@autone/ui';
import { NoDataMessage } from '@autone/ui-next/components';
import { PAGE_SIZE_OPTIONS } from '@autone/utils';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { type APP_NAME } from '../../../consts';
import { inSeasonSearchTermAtom } from '../../../storage/atoms';
import { useAssortmentSessionStorageAtom } from '../../../storage/session-storage';
import { trackAssortmentTableEvents } from '../analytics';
import { AssortmentTableContextProvider } from '../contexts';

import { SkuLocationsTableActionBar } from './components/SkuLocationsTableActionBar';
import { useGetSkuLocationsTableQueryArgs } from './hooks/useGetSkuLocationsTableQueryArgs';
import { skuLocationsTableColumnDefs } from './SkuLocationsTable.columns';
import { SkuLocationsPaginatedSelectionContextProvider } from './SkuLocationsTable.context';
import { type SkuLocationsTableColumnId } from './SkuLocationsTable.types';
import {
  type AssortmentSkuLocationsSortKeys,
  sortableColumnToFieldIdMap,
  usePaginatedAssortmentSkuLocationsQuery,
} from './SkuLocationsTable.utilities';
import { getRowId } from './utilities/row-id';

export const SkuLocationsTable = () => {
  const { common } = useAutoneTranslation<typeof APP_NAME>();

  const { sortBy, sortingState, setSortingState } = useServerSorting<
    AssortmentSkuLocationsSortKeys,
    SkuLocationsTableColumnId
  >(undefined, false, sortableColumnToFieldIdMap);

  const searchTerm = useAtomValue(inSeasonSearchTermAtom);
  const [{ customColumns }] = useAssortmentSessionStorageAtom(
    'skuLocationsTable.extrasMenu',
  );

  const queryArgs = useGetSkuLocationsTableQueryArgs({ searchTerm, sortBy });

  const {
    currentPage,
    getNextPage,
    getPrevPage,
    paginationState: { pageIndex, canGetNextPage, canGetPrevPage, pageSize },
    setPageSize,
    infiniteQueryResults: { isFetching, isLoading, isUninitialized },
  } = usePaginatedAssortmentSkuLocationsQuery(queryArgs, {
    initialPageSize: PAGE_SIZE_OPTIONS[1],
    initialPageIndex: 0,
    refetchOnMountOrArgChange: true,
  });

  useDebounceEffect(
    () => {
      const { query, body } = queryArgs;
      const { sort_by } = query;
      const { filters, advanced_filter } = body;
      const filterDimensionsApplied = Object.keys(filters);
      const advancedFilterFieldsApplied = advanced_filter?.conditions.map(
        (condition) => condition.field,
      );
      trackAssortmentTableEvents({
        tableName: 'sku_locations_table',
        sortBy: sort_by,
        filterDimensionsApplied,
        advancedFilterFieldsApplied,
      });
    },
    [queryArgs],
    300,
  );

  const { memoisedData, getRowIdLoadingGuard } = useDataTableLoadingGuard({
    mode: 'dynamic',
    isLoading,
    isFetching,
    isUninitialized,
    data: currentPage?.items,
    initialRowCount: pageSize,
  });
  const totalRows = useMemo(
    () => currentPage?.page_info.total_count ?? 0,
    [currentPage],
  );

  const { columnOrder, columnVisibility } = useColumnVisibilityAndOrderState(
    skuLocationsTableColumnDefs,
    customColumns,
  );

  const table = useReactTable({
    data: memoisedData,
    columns: skuLocationsTableColumnDefs,
    getRowId: getRowIdLoadingGuard(getRowId),
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSortingState,
    enableMultiSort: false,
    state: { sorting: sortingState, columnOrder, columnVisibility },
  });

  const hasVisibleRows = table.getRowModel().rows.length > 0;
  const shouldDisplayNoData = !hasVisibleRows && !isFetching;

  const currentRows = table.getRowModel().rows;
  const currentRowIds = useMemo(
    () => currentRows.map((row) => row.id),
    [currentRows],
  );

  return (
    <AssortmentTableContextProvider table="skuLocationsTable">
      <SkuLocationsPaginatedSelectionContextProvider
        currentRowIds={currentRowIds}
      >
        <DataTableContainer className="rounded-none border-none">
          <DataTableContainerContent className="max-h-[70vh]">
            <DataTable
              id="sku-locations-table"
              data-testid="sku-locations-table"
              showScrollbar={!shouldDisplayNoData}
              className="min-w-full table-fixed"
            >
              <DataTableHeader className="z-50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <DataTableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <DataTableHead
                          id={`header-cell-${header.id}`}
                          data-testid={`header-cell-${header.id}`}
                          style={{ width: header.getSize() }}
                          key={header.id}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </DataTableHead>
                      );
                    })}
                  </DataTableRow>
                ))}
              </DataTableHeader>
              <DataTableBody>
                {table.getRowModel().rows.map((row) => (
                  <DataTableRow key={row.id} data-testid={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <DataTableCell key={cell.id} className="h-16">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </DataTableCell>
                    ))}
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
            {shouldDisplayNoData && (
              <div className="grow">
                <NoDataMessage
                  title={common('no-table-data-title')}
                  subtitle={common('no-table-data-subtitle')}
                />
              </div>
            )}
          </DataTableContainerContent>
          <DataTableContainerFooter>
            <DataTablePagination
              totalRecords={totalRows}
              onGetNextPage={getNextPage}
              onGetPreviousPage={getPrevPage}
              canGetNextPage={canGetNextPage}
              canGetPreviousPage={canGetPrevPage}
              currentPageIndex={pageIndex}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              currPageSize={pageSize}
              onPageSizeChange={setPageSize}
            />
          </DataTableContainerFooter>
        </DataTableContainer>
        <SkuLocationsTableActionBar />
      </SkuLocationsPaginatedSelectionContextProvider>
    </AssortmentTableContextProvider>
  );
};
